import type { WhatsAppTemplatePresentation } from "@multi-chat/shared";
import {
  canSubmitMarketingDefaultTemplate,
  voiceCallTtlMinutesToDays,
  VOICE_CALL_TTL_MINUTES_DEFAULT,
} from "../../template-editor-presentation.js";
import {
  templateDetailsCategoryLabelKey,
  templateDetailsLanguageLabel,
  templateDetailsUseCaseTitleKey,
  templateDetailsVariableTypeFieldKey,
  templateDetailsVariableTypeValueKey,
  type TemplateListDetailsButton,
  type TemplateListDetailsButtonKind,
  type TemplateListDetailsContentBlock,
} from "../../template-list-presentation.js";
import type {
  TemplateCreationCategory,
  TemplateCreationStep,
  TemplateCreationUseCase,
} from "../template-creation-wizard-state.js";
import {
  buildFormTemplatePresentation,
  extractPlaceholderKeys,
  type TemplateFormState,
} from "../template-management-form.js";

export interface TemplateReviewModel {
  name: string;
  languageLabel: string;
  categoryLabelKey: string;
  useCaseTitleKey: string;
  variableTypeFieldKey: string;
  variableTypeValueKey: string;
  contentBlocks: TemplateListDetailsContentBlock[];
  examples: Array<{ scope: "header" | "body"; token: string; value: string }>;
  buttons: TemplateListDetailsButton[];
  preview: WhatsAppTemplatePresentation;
}

function buttonTypeLabelKey(kind: TemplateListDetailsButtonKind): string {
  return `whatsapp.templates.list.detailPanel.buttonTypes.${kind}`;
}

function buildReviewButtons(
  formState: TemplateFormState,
): TemplateListDetailsButton[] {
  return formState.buttons.map((button, index) => {
    const key = button.clientId ?? `button-${index}`;
    switch (button.type) {
      case "QUICK_REPLY":
        return {
          key,
          kind: "QUICK_REPLY",
          typeLabelKey: buttonTypeLabelKey("QUICK_REPLY"),
          label: button.text,
        };
      case "URL": {
        const example = formState.urlButtonExamples[index] ?? button.example ?? "";
        return {
          key,
          kind: "URL",
          typeLabelKey: buttonTypeLabelKey("URL"),
          label: button.text,
          ...(button.url
            ? {
                secondary: example ? `${button.url}${example}` : button.url,
                secondaryKind: "url" as const,
              }
            : {}),
        };
      }
      case "PHONE_NUMBER":
        return {
          key,
          kind: "PHONE_NUMBER",
          typeLabelKey: buttonTypeLabelKey("PHONE_NUMBER"),
          label: button.text,
          ...(button.phoneNumber
            ? { secondary: button.phoneNumber, secondaryKind: "phone" as const }
            : {}),
        };
      case "VOICE_CALL":
        return {
          key,
          kind: "VOICE_CALL",
          typeLabelKey: buttonTypeLabelKey("VOICE_CALL"),
          label: button.text,
          secondary: String(
            voiceCallTtlMinutesToDays(
              button.ttlMinutes ?? VOICE_CALL_TTL_MINUTES_DEFAULT,
            ),
          ),
          secondaryKind: "ttl",
        };
      default: {
        const _exhaustive: never = button;
        return _exhaustive;
      }
    }
  });
}

function buildReviewContentBlocks(
  formState: TemplateFormState,
): TemplateListDetailsContentBlock[] {
  const blocks: TemplateListDetailsContentBlock[] = [];
  if (formState.hasHeader && formState.headerText.trim()) {
    blocks.push({
      key: "header",
      kind: "header",
      text: formState.headerText,
    });
  }
  if (formState.bodyText.trim()) {
    blocks.push({
      key: "body",
      kind: "body",
      text: formState.bodyText,
    });
  }
  if (formState.hasFooter && formState.footerText.trim()) {
    blocks.push({
      key: "footer",
      kind: "footer",
      text: formState.footerText,
    });
  }
  return blocks;
}

function buildReviewExamples(
  formState: TemplateFormState,
): TemplateReviewModel["examples"] {
  const examples: TemplateReviewModel["examples"] = [];
  if (formState.hasHeader) {
    for (const key of extractPlaceholderKeys(formState.headerText)) {
      const value = formState.headerExample.trim();
      if (!value) continue;
      examples.push({ scope: "header", token: `{{${key}}}`, value });
    }
  }
  for (const key of extractPlaceholderKeys(formState.bodyText)) {
    const value = (formState.bodyExamples[key] ?? "").trim();
    if (!value) continue;
    examples.push({ scope: "body", token: `{{${key}}}`, value });
  }
  return examples;
}

/** Review-only model derived from live form state (no stale snapshot). */
export function buildTemplateReviewModel(input: {
  formState: TemplateFormState;
  category: TemplateCreationCategory;
  useCase: TemplateCreationUseCase;
}): TemplateReviewModel {
  const { formState, category, useCase } = input;
  return {
    name: formState.name,
    languageLabel: templateDetailsLanguageLabel(formState.language),
    categoryLabelKey: templateDetailsCategoryLabelKey(category),
    useCaseTitleKey: templateDetailsUseCaseTitleKey(useCase),
    variableTypeFieldKey: templateDetailsVariableTypeFieldKey(),
    variableTypeValueKey: templateDetailsVariableTypeValueKey(
      formState.parameterFormat,
    ),
    contentBlocks: buildReviewContentBlocks(formState),
    examples: buildReviewExamples(formState),
    buttons: buildReviewButtons(formState),
    preview: buildFormTemplatePresentation(formState),
  };
}

/** Create-only: Step 2 may advance to REVIEW when create validation passes. */
export function canAdvanceCreateEditToReview(formState: TemplateFormState): boolean {
  return canSubmitMarketingDefaultTemplate(formState);
}

export function nextCreateWizardStep(
  current: TemplateCreationStep,
  options: { editValid: boolean },
): TemplateCreationStep | null {
  switch (current) {
    case "DEFINITION":
      return "EDIT";
    case "EDIT":
      return options.editValid ? "REVIEW" : null;
    case "REVIEW":
      return null;
    default: {
      const _exhaustive: never = current;
      return _exhaustive;
    }
  }
}

export function previousCreateWizardStep(
  current: TemplateCreationStep,
): TemplateCreationStep | null {
  switch (current) {
    case "REVIEW":
      return "EDIT";
    case "EDIT":
      return "DEFINITION";
    case "DEFINITION":
      return null;
    default: {
      const _exhaustive: never = current;
      return _exhaustive;
    }
  }
}
